import { idb } from "../db/index.ts";
import { g, local, updatePlayMenu } from "../util/index.ts";
import type { UpdateEvents, ViewInput } from "../../common/types.ts";
import { SIMPLE_AWARDS } from "../../common/constants.ts";
import { bySport } from "../../common/sportFunctions.ts";
import { processPlayersHallOfFame } from "../util/processPlayersHallOfFame.ts";

const viewedSeasonSummary = async () => {
	local.unviewedSeasonSummary = false;
	await updatePlayMenu();
};

const updateHistory = async (
	{ season }: ViewInput<"history">,
	updateEvents: UpdateEvents,
	state: any,
) => {
	if (local.unviewedSeasonSummary) {
		viewedSeasonSummary();
	}

	if (updateEvents.includes("firstRun") || state.season !== season) {
		const awards = await idb.getCopy.awards({
			season,
		});

		if (!awards) {
			viewedSeasonSummary(); // Should never happen, but just in case

			// https://stackoverflow.com/a/59923262/786644
			const returnValue = {
				invalidSeason: true as const,
				season,
			};
			return returnValue;
		}

		const teams = await idb.getCopies.teamsPlus(
			{
				attrs: ["tid"],
				seasonAttrs: ["playoffRoundsWon", "abbrev", "region", "name"],
				season,
			},
			"noCopyCache",
		);

		const addAbbrev = (obj: any) => {
			// Not sure why this would ever be null, but somebody said it was
			if (obj == undefined) {
				return;
			}

			const t = teams.find((t) => t.tid === obj.tid);
			if (t) {
				obj.abbrev = t.seasonAttrs.abbrev;
			} else {
				obj.abbrev = "???";
			}
		};

		for (const key of SIMPLE_AWARDS) {
			addAbbrev(awards[key]);
		}
		const possibleTeamAwards = ["allLeague", "allDefensive"];
		for (const key of possibleTeamAwards) {
			if (awards[key]) {
				for (const team of awards[key]) {
					for (const p of team.players) {
						addAbbrev(p);
					}
				}
			}
		}
		const flatTeams = bySport({
			baseball: ["allRookie", "allOffense", "allDefense"],
			basketball: ["allRookie", "sfmvp"],
			football: ["allRookie"],
			hockey: ["allRookie"],
		});
		for (const key of flatTeams) {
			if (awards[key]) {
				for (const p of awards[key]) {
					addAbbrev(p);
				}
			}
		}

		// Hack placeholder for old seasons
		if (!awards.allRookie) {
			awards.allRookie = [];
		}

		// For old league files, this format is obsolete now
		if (awards && awards.bre && awards.brw) {
			awards.bestRecordConfs = [awards.bre, awards.brw];
		}

		const retiredPlayersAll = await idb.getCopies.players(
			{
				retiredYear: season,
			},
			"noCopyCache",
		);
		const retiredStat = bySport({
			baseball: "war",
			basketball: "ws",
			football: "av",
			hockey: "ps",
		});
		const retiredPlayers = processPlayersHallOfFame(
			await idb.getCopies.playersPlus(retiredPlayersAll, {
				attrs: ["pid", "name", "age", "hof"],
				ratings: ["pos", "season"],
				stats: ["season", "tid", "abbrev", retiredStat],
				showNoStats: true,
			}),
		).map((p) => {
			const lastStats = p.stats.at(-1);

			return {
				pid: p.pid,
				name: p.name,
				age: p.age,
				hof: p.hof,
				pos: p.bestPos,
				t:
					lastStats?.season === season
						? {
								tid: lastStats.tid,
								abbrev: lastStats.abbrev,
							}
						: undefined,
				stat: p.careerStats[retiredStat],
			};
		});
		console.log(retiredPlayers);
		retiredPlayers.sort((a, b) => b.stat - a.stat);

		// Get champs
		const champ: any = teams.find(
			(t) =>
				t.seasonAttrs.playoffRoundsWon ===
				g.get("numGamesPlayoffSeries", season).length,
		);

		// Enrich the champion with their regular-season and postseason record, team rating, and
		// scoring, so the recap screen can unfurl the champ's full season line.
		if (champ) {
			const seasonLine = async (playoffs: boolean) => {
				const [t] = await idb.getCopies.teamsPlus(
					{
						attrs: ["tid"],
						seasonAttrs: ["won", "lost", "ovrEnd"],
						stats: ["gp", "pts", "oppPts"],
						season,
						tid: champ.tid,
						playoffs,
						regularSeason: !playoffs,
					},
					"noCopyCache",
				);
				return t;
			};

			const reg = await seasonLine(false);
			const po = await seasonLine(true);

			// Regular-season W/L comes straight from teamSeason; playoff W/L must be tallied from
			// the bracket (seasonAttrs.won/lost are regular season only).
			let poWon = 0;
			let poLost = 0;
			const playoffSeries = await idb.getCopy.playoffSeries({ season });
			if (playoffSeries) {
				for (const round of playoffSeries.series) {
					for (const matchup of round) {
						const { home, away } = matchup;
						if (home && home.tid === champ.tid) {
							poWon += home.won ?? 0;
							poLost += away?.won ?? 0;
						} else if (away && away.tid === champ.tid) {
							poWon += away.won ?? 0;
							poLost += home?.won ?? 0;
						}
					}
				}
			}

			champ.seasonLine = {
				ovr: reg?.seasonAttrs.ovrEnd,
				reg: {
					won: reg?.seasonAttrs.won ?? 0,
					lost: reg?.seasonAttrs.lost ?? 0,
					pts: reg?.stats.pts ?? 0,
					oppPts: reg?.stats.oppPts ?? 0,
				},
				po: {
					won: poWon,
					lost: poLost,
					pts: po?.stats.pts ?? 0,
					oppPts: po?.stats.oppPts ?? 0,
				},
			};
		}

		return {
			awards,
			champ,
			confs: g.get("confs", season),
			invalidSeason: false as const,
			retiredPlayers,
			retiredStat,
			season,
		};
	}
};

export default updateHistory;
